import { useEffect, ReactNode, useRef, useMemo } from 'react';
import './style.css';
import { useUi } from '@weareconceptstudio/core';

// Lazy load GSAP plugins only when needed
let gsapInstance = null;
let scrollTriggerInstance = null;
let splitTextInstance = null;

const loadGSAP = async () => {
	if (gsapInstance) {
		return { gsap: gsapInstance, ScrollTrigger: scrollTriggerInstance, SplitText: splitTextInstance };
	}

	const [gsapModule, ScrollTriggerModule, SplitTextModule] = await Promise.all([import('gsap'), import('gsap/ScrollTrigger'), import('gsap/SplitText')]);
	const gsapLib = (gsapModule as any).gsap || (gsapModule as any).default || gsapModule;
	const scrollTriggerLib = (ScrollTriggerModule as any).ScrollTrigger || (ScrollTriggerModule as any).default || ScrollTriggerModule;
	const splitTextLib = (SplitTextModule as any).SplitText || (SplitTextModule as any).default || SplitTextModule;

	gsapInstance = gsapLib;
	scrollTriggerInstance = scrollTriggerLib;
	splitTextInstance = splitTextLib;
	gsapInstance.registerPlugin(scrollTriggerInstance, splitTextInstance);

	return { gsap: gsapInstance, ScrollTrigger: scrollTriggerInstance, SplitText: splitTextInstance };
};

type AnimType = 'title' | 'subtitle';

interface TitleAnimProps {
	children: ReactNode;
	animType?: AnimType;
	animRef: React.RefObject<HTMLElement>;
	triggerRef?: React.RefObject<HTMLElement>;
	startedAnim?: boolean | number;

	setVars?: gsap.TweenVars;
	toVars?: gsap.TweenVars;
	finishPercent?: number;
	onFinish?: (done: boolean) => void;
	onBack?: () => void;

	configAnim?: gsap.TweenVars;
	configTrigger?: ScrollTrigger.StaticVars;

	resize?: boolean;
	revertNone?: boolean;
	completedRevert?: boolean;
}

const TitleAnim: React.FC<TitleAnimProps> = ({
	children,
	animType = 'title',
	animRef,
	triggerRef: externalTriggerRef,
	startedAnim = true,
	setVars = {},
	toVars = {},
	finishPercent = 0.9,
	onFinish,
	onBack,
	configAnim = {},
	configTrigger = {},
	resize = true,
	revertNone = false,
	completedRevert = false,
}) => {
	const { isMobile } = useUi();

	//* Refs for cleanup
	const splitTextRef = useRef<SplitText | null>(null);
	const splitInitialRef = useRef<SplitText | null>(null);
	const scrollTriggerRef = useRef<ScrollTrigger | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const tweenRef = useRef<gsap.core.Tween | null>(null);
	const revertTriggerRef = useRef<ScrollTrigger | null>(null);

	//* Memoized animation configurations
	const animConfigs = useMemo(
		() => ({
			title: {
				set: { rotateZ: 10, transformOrigin: 'left', y: '100%' },
				to: { y: 0, rotateZ: 0 },
				tween: { stagger: 0.15, duration: 0.8, ease: 'power2.out' },
			},
			subtitle: isMobile
				? {
						set: { opacity: 0 },
						to: { opacity: 1 },
						tween: { stagger: 0.11, duration: 1.1, ease: 'power1.out' },
					}
				: {
						set: { scale: 0, transformOrigin: 'bottom' },
						to: { scale: 1, rotateZ: 0 },
						tween: { stagger: 0.003, duration: 0.6, ease: 'power2.out', force3D: true },
					},
		}),
		[isMobile]
	);

	//* Initialize animation
	useEffect(() => {
		if (!animRef?.current) return;

		let isActive = true;
		let context: gsap.Context | null = null;

		const initialize = async () => {
			try {
				const { gsap, ScrollTrigger, SplitText } = await loadGSAP();

				if (!isActive || !animRef.current) return;

				if (animRef.current.classList.contains('opacity-animation')) {
					animRef.current.classList.remove('opacity-animation');
				}
				context = gsap.context(() => {
					const triggerElement = externalTriggerRef?.current || animRef.current;
					const config = animConfigs[animType] ?? {
						set: {},
						to: {},
						tween: { stagger: 0.1, duration: 0.5, ease: 'power2.out' },
					};

					const defaultTrigger: ScrollTrigger.StaticVars = {
						trigger: triggerElement,
						start: 'top bottom',
						end: 'bottom top',
						// markers: true,
					};

					//* Pre-wrap and clean HTML
					if (animRef.current) {
						animRef.current.style.whiteSpace = 'pre-wrap';
						const el = animRef.current;
						el.innerHTML = el.innerText.replace(/\n{2,}/g, (m) => '<br />'.repeat(m.length)).replace(/\s*\n\s*/g, ' ');
					}

					//* Create ScrollTrigger (SplitText создаём на входе, убираем при выходе сверху)
					if (animRef.current) {
						const runAnimation = () => {
							if (!animRef.current || !startedAnim) return;

							// если уже есть запущенный твийн — убиваем его,
							// чтобы при быстром заходе/выходе не было "висящей" старой анимации
							if (tweenRef.current) {
								tweenRef.current.kill();
								tweenRef.current = null;
							}

							// очистить предыдущие SplitText, если были
							if (splitTextRef.current) {
								splitTextRef.current.revert();
								splitTextRef.current = null;
							}
							if (splitInitialRef.current) {
								splitInitialRef.current.revert();
								splitInitialRef.current = null;
							}

							// создать новые SplitText-обёртки
							splitInitialRef.current = new SplitText(animRef.current, {
								type: 'lines',
								linesClass: 'line-initial',
								reduceWhiteSpace: false,
							});

							splitTextRef.current = new SplitText(splitInitialRef.current.lines, {
								type: animType === 'title' ? 'lines' : isMobile ? 'words' : 'words chars',
								linesClass: 'lines',
								charsClass: 'char',
								wordsClass: 'words',
								reduceWhiteSpace: false,
							});

							const target = animType === 'title' ? splitTextRef.current.lines : isMobile ? splitInitialRef.current.lines : splitTextRef.current.chars;

							// начальное состояние
							gsap.set(target, {
								...config.set,
								...setVars,
							});

							let fired = false;

							const userOnUpdate = (configAnim as any).onUpdate;

							tweenRef.current = gsap.to(target, {
								...config.to,
								...toVars,
								...config.tween,
								...configAnim,
								onUpdate: function () {
									if (!fired && onFinish && this.progress() >= finishPercent) {
										fired = true;
										onFinish(true);
									}

									if (typeof userOnUpdate === 'function') {
										userOnUpdate.call(this);
									}
								},
								onComplete: () => {
									if (completedRevert) {
										resetAnimation();
									}
								},
							});

							if (animRef.current) {
								gsap.set(animRef.current, { opacity: 1 });
							}
						};

						const resetAnimation = () => {
							if (tweenRef.current) {
								tweenRef.current.kill();
								tweenRef.current = null;
							}

							if (splitTextRef.current) {
								splitTextRef.current.revert();
								splitTextRef.current = null;
							}
							if (splitInitialRef.current) {
								splitInitialRef.current.revert();
								splitInitialRef.current = null;
							}

							if (animRef.current) {
								gsap.set(animRef.current, { opacity: 1 });
							}
						};

						revertTriggerRef.current = revertNone
							? null
							: ScrollTrigger.create({
									trigger: animRef.current,
									start: 'top bottom',
									end: 'bottom top',
									// markers: true,
									onLeave: () => {
										resetAnimation();
									},
									onEnterBack: () => {},
								});

						scrollTriggerRef.current = ScrollTrigger.create({
							...defaultTrigger,
							...configTrigger,
							onEnter: () => {
								runAnimation();
							},
							onEnterBack: () => {
								if (tweenRef.current) {
									tweenRef.current.kill();
									tweenRef.current = null;
								}
								// Убедиться, что SplitText снят и DOM обычный
								if (splitTextRef.current) {
									splitTextRef.current.revert();
									splitTextRef.current = null;
								}
								if (splitInitialRef.current) {
									splitInitialRef.current.revert();
									splitInitialRef.current = null;
								}
								if (animRef.current) {
									gsap.set(animRef.current, { opacity: 1 });
								}
							},

							onLeaveBack: () => {
								if (onBack) {
									onBack();
								}
							},
						});

						//* Setup ResizeObserver if needed
						if (resize && typeof ResizeObserver !== 'undefined') {
							resizeObserverRef.current = new ResizeObserver(() => {
								if (resizeTimeoutRef.current) {
									clearTimeout(resizeTimeoutRef.current);
								}

								resizeTimeoutRef.current = setTimeout(() => {
									if (scrollTriggerRef.current) {
										scrollTriggerRef.current.refresh();
									}
									if (revertTriggerRef.current) {
										revertTriggerRef.current.refresh();
									}
								}, 150);
							});

							//* Observe only the specific element, not the entire main
							const elementToObserve = animRef.current.closest('main') || animRef.current;
							resizeObserverRef.current.observe(elementToObserve);
						}
					}
				}, animRef.current);
			} catch (error) {
				console.error('[TitleAnim] Failed to initialize Title animation:', error);
			}
		};

		initialize();

		return () => {
			isActive = false;

			// Kill tween if still active
			if (tweenRef.current) {
				tweenRef.current.kill();
				tweenRef.current = null;
			}

			// Kill ScrollTriggers
			if (scrollTriggerRef.current) {
				scrollTriggerRef.current.kill();
				scrollTriggerRef.current = null;
			}

			if (revertTriggerRef.current) {
				revertTriggerRef.current.kill();
				revertTriggerRef.current = null;
			}

			// Cleanup SplitText instances
			if (splitTextRef.current) {
				splitTextRef.current.revert();
				splitTextRef.current = null;
			}

			if (splitInitialRef.current) {
				splitInitialRef.current.revert();
				splitInitialRef.current = null;
			}

			// Disconnect ResizeObserver
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect();
				resizeObserverRef.current = null;
			}

			// Clear resize timeout
			if (resizeTimeoutRef.current) {
				clearTimeout(resizeTimeoutRef.current);
				resizeTimeoutRef.current = null;
			}

			if (context) {
				context.revert();
				context = null;
			}
		};
	}, [startedAnim, isMobile]);

	return children;
};

export default TitleAnim;
